import { HashRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { learningOsRoutes, learningStagePath, legacyRouteRedirects } from './app/routes.js'
import { AuthProvider } from './auth.jsx'
import AppLayout from './components/AppLayout.jsx'
import RequireAuth from './components/RequireAuth.jsx'
import RequireLearningPlan from './components/RequireLearningPlan.jsx'
import ScrollToTop from './components/ScrollToTop.jsx'
import { LanguageProvider } from './contexts/LanguageContext.jsx'
import AdminPage from './pages/AdminPage.jsx'
import AllTimePage from './pages/AllTimePage.jsx'
import { AudienceSectionPage } from './pages/AudienceSectionPage.jsx'
import CareerPlannerPage from './pages/CareerPlannerPage.jsx'
import ComingSoonPage from './pages/ComingSoonPage.jsx'
import DeveloperHomePage from './pages/DeveloperHomePage.jsx'
import DeveloperToolDetailPage from './pages/DeveloperToolDetailPage.jsx'
import FavoritesPage from './pages/FavoritesPage.jsx'
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx'
import GithubOAuthCallbackPage from './pages/GithubOAuthCallbackPage.jsx'
import GithubAnalysisSharePage from './pages/GithubAnalysisSharePage.jsx'
import GrowthAssessmentPage from './pages/GrowthAssessmentPage.jsx'
import GrowthCompanionPage from './pages/GrowthCompanionPage.jsx'
import HomePage from './pages/HomePage.jsx'
import JobAssistantPage from './pages/JobAssistantPage.jsx'
import LoginPage from './pages/LoginPage.jsx'
import MyGithubPage from './pages/MyGithubPage.jsx'
import NotFoundPage from './pages/NotFoundPage.jsx'
import ProfilePage from './pages/ProfilePage.jsx'
import ProjectHealthPage from './pages/ProjectHealthPage.jsx'
import RegisterPage from './pages/RegisterPage.jsx'
import ResetPasswordPage from './pages/ResetPasswordPage.jsx'
import RepoDetailPage from './pages/RepoDetailPage.jsx'
import SearchPage from './pages/SearchPage.jsx'
import AiToolsPage from './pages/more/AiToolsPage.jsx'
import CertificatesPage from './pages/more/CertificatesPage.jsx'
import CompetitionsPage from './pages/more/CompetitionsPage.jsx'
import DashboardPage from './pages/dashboard/DashboardPage.jsx'
import ExercisePage from './pages/exercise/ExercisePage.jsx'
import GrowthPage from './pages/growth/GrowthPage.jsx'
import GrowthTimelinePage from './pages/growth/GrowthTimelinePage.jsx'
import GrowthPlanPage from './pages/growth/GrowthPlanPage.jsx'
import LearningPage from './pages/learning/LearningPage.jsx'
import LearningPracticePage from './pages/learning/LearningPracticePage.jsx'
import LearningResourceDetailPage from './pages/learning/LearningResourceDetailPage.jsx'
import LearningStepPage from './pages/learning/LearningStepPage.jsx'
import AiLearningExercisePage from './pages/learning/AiLearningExercisePage.jsx'
import StudentTodayPage from './pages/learning/StudentTodayPage.jsx'
import LearningOsHomePage from './pages/home/HomePage.jsx'
import MorePage from './pages/more/MorePage.jsx'
import OnboardingPage from './pages/onboarding/OnboardingPage.jsx'
import ProjectDetailPage from './pages/project/ProjectDetailPage.jsx'
import ProjectPage from './pages/project/ProjectPage.jsx'
import ProjectRoadmapPage from './pages/project/ProjectRoadmapPage.jsx'
import ProjectsHomePage from './pages/projects/ProjectsHomePage.jsx'
import ResourceDetailPage from './pages/resource/ResourceDetailPage.jsx'
import ResourcePage from './pages/resource/ResourcePage.jsx'
import ResumePage from './pages/more/ResumePage.jsx'
import RoadmapPage from './pages/roadmap/RoadmapPage.jsx'
import RoadmapStagePage from './pages/roadmap/RoadmapStagePage.jsx'
import InterviewPackPage from './pages/more/InterviewPackPage.jsx'
import InterviewPage from './pages/more/InterviewPage.jsx'
import StudentMajorCareerPage from './pages/StudentMajorCareerPage.jsx'
import StudentCareerExplorePage from './pages/StudentCareerExplorePage.jsx'
import StudentGrowthAssetsPage from './pages/StudentGrowthAssetsPage.jsx'
import StudentGrowthCenterPage from './pages/StudentGrowthCenterPage.jsx'
import StudentProjectWorkspace from './pages/StudentProjectWorkspace.jsx'
import StudentProjectDetailPage from './pages/StudentProjectDetailPage.jsx'
import StudentProjectLibraryPage from './pages/StudentProjectLibraryPage.jsx'
import StudentProjectsPage from './pages/StudentProjectsPage.jsx'
import SupportPage from './pages/SupportPage.jsx'
import ToolDetailPage from './pages/ToolDetailPage.jsx'
import V2HomePage from './pages/V2HomePage.jsx'
import WeeklyPage from './pages/WeeklyPage.jsx'
import VerifyEmailPage from './pages/VerifyEmailPage.jsx'
import VipOrderPage from './pages/VipOrderPage.jsx'
import VipPage from './pages/VipPage.jsx'

function LegacyLearningStageRedirect() {
  const { stageId } = useParams()
  return <Navigate to={learningStagePath(stageId)} replace />
}

function LegacyLearningResourceRedirect() {
  const { resourceId } = useParams()
  return <Navigate to={`/learning/resources/${encodeURIComponent(resourceId || '')}`} replace />
}

function LegacyPracticeRedirect() {
  const { practiceId } = useParams()
  return <Navigate to={practiceId ? `/learning/practice/${encodeURIComponent(practiceId)}` : learningOsRoutes.learning} replace />
}

function LegacyResourceRedirect() {
  const { resourceId } = useParams()
  return <Navigate to={`/resource/${encodeURIComponent(resourceId || '')}`} replace />
}

function LegacyCareerPrepareRedirect() {
  const { direction } = useParams()
  return <Navigate to={`/career-assistant${direction ? `?direction=${encodeURIComponent(direction)}` : ''}`} replace />
}

function LegacyProjectRedirect() {
  const { projectId } = useParams()
  return <Navigate to={`/projects/${encodeURIComponent(projectId || '')}`} replace />
}

function LegacyProjectRoadmapRedirect() {
  const { projectId } = useParams()
  return <Navigate to={`/projects/${encodeURIComponent(projectId || '')}/roadmap`} replace />
}

function LegacyExploreRedirect() {
  const location = useLocation()
  return <Navigate to={`${learningOsRoutes.explore}${location.search || ''}`} replace />
}

function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <LanguageProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/github/callback" element={<GithubOAuthCallbackPage />} />
            <Route element={<AppLayout />}>
              <Route path={learningOsRoutes.home} element={<LearningOsHomePage />} />
              <Route path="/home" element={<LearningOsHomePage />} />
              <Route path={legacyRouteRedirects.start} element={<Navigate to={learningOsRoutes.onboarding} replace />} />
              <Route path={legacyRouteRedirects.studentStart} element={<Navigate to={learningOsRoutes.onboarding} replace />} />
              <Route path={learningOsRoutes.onboarding} element={<OnboardingPage />} />
              <Route path={legacyRouteRedirects.dashboard} element={<Navigate to={learningOsRoutes.growth} replace />} />
              <Route path={learningOsRoutes.growth} element={<GrowthPage />} />
              <Route path="/growth/report" element={<StudentGrowthCenterPage />} />
              <Route path="/growth/timeline" element={<GrowthTimelinePage />} />
              <Route path="/growth/assets" element={<StudentGrowthAssetsPage />} />
              <Route path="/learning-dashboard" element={<RequireLearningPlan><DashboardPage /></RequireLearningPlan>} />
              <Route path={learningOsRoutes.learning} element={<LearningPage />} />
              <Route path={learningOsRoutes.learningPath} element={<LearningPage />} />
              <Route path={legacyRouteRedirects.studentLearning} element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path={legacyRouteRedirects.studentLearningPaths} element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path={legacyRouteRedirects.studentLearningPathDetail} element={<LearningPage />} />
              <Route path={legacyRouteRedirects.studentLearningNode} element={<LegacyLearningStageRedirect />} />
              <Route path="/student/learning/resources/:resourceId" element={<LegacyLearningResourceRedirect />} />
              <Route path="/learning/resources/:resourceId" element={<LearningResourceDetailPage />} />
              <Route path="/learning/practice/:practiceId" element={<LearningPracticePage />} />
              <Route path="/today" element={<StudentTodayPage />} />
              <Route path="/student/today" element={<Navigate to="/today" replace />} />
              <Route path="/student/practice" element={<LegacyPracticeRedirect />} />
              <Route path="/student/practice/:practiceId" element={<LegacyPracticeRedirect />} />
              <Route path="/learning/ai/:aiNodeId/exercise" element={<AiLearningExercisePage />} />
              <Route path={learningOsRoutes.aiLearningNode} element={<LearningStepPage />} />
              <Route path={learningOsRoutes.learningStage} element={<LearningStepPage />} />
              <Route path={legacyRouteRedirects.roadmap} element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path={learningOsRoutes.roadmap} element={<RequireLearningPlan><RoadmapPage /></RequireLearningPlan>} />
              <Route path={learningOsRoutes.roadmapStage} element={<RequireLearningPlan><RoadmapStagePage /></RequireLearningPlan>} />
              <Route path={learningOsRoutes.resource} element={<ResourcePage />} />
              <Route path={learningOsRoutes.resourceDetail} element={<ResourceDetailPage />} />
              <Route path={learningOsRoutes.exercise} element={<ExercisePage />} />
              <Route path="/project" element={<Navigate to={learningOsRoutes.project} replace />} />
              <Route path="/project/:projectId" element={<LegacyProjectRedirect />} />
              <Route path="/project/:projectId/roadmap" element={<LegacyProjectRoadmapRedirect />} />
              <Route path="/project-recommendations" element={<ProjectPage />} />
              <Route path="/project-recommendations/:projectId" element={<ProjectDetailPage />} />
              <Route path={learningOsRoutes.project} element={<ProjectsHomePage />} />
              <Route path="/projects/my" element={<StudentProjectsPage />} />
              <Route path="/projects/recommendations" element={<StudentProjectLibraryPage />} />
              <Route path="/projects/resume-assets" element={<ResumePage />} />
              <Route path={learningOsRoutes.projectDetail} element={<StudentProjectDetailPage />} />
              <Route path={learningOsRoutes.projectRoadmap} element={<ProjectRoadmapPage />} />
              <Route path={learningOsRoutes.projectLibrary} element={<StudentProjectLibraryPage />} />
              <Route path={learningOsRoutes.projectWorkspace} element={<StudentProjectWorkspace />} />
              <Route path={learningOsRoutes.growthPlan} element={<GrowthPlanPage />} />
              <Route path="/tools" element={<V2HomePage />} />
              <Route path={legacyRouteRedirects.studentHome} element={<Navigate to={learningOsRoutes.growth} replace />} />
              <Route path="/student/growth-assessment" element={<Navigate to="/growth-assessment" replace />} />
              <Route path="/growth-assessment" element={<GrowthAssessmentPage />} />
              <Route path={legacyRouteRedirects.studentGrowthCompanion} element={<Navigate to={learningOsRoutes.aiPartner} replace />} />
              <Route path={learningOsRoutes.aiPartner} element={<GrowthCompanionPage />} />
              <Route path="/student/profile-setup" element={<Navigate to={learningOsRoutes.aiPartner} replace />} />
              <Route path={legacyRouteRedirects.studentCareerExplore} element={<Navigate to={learningOsRoutes.explore} replace />} />
              <Route path="/career-explore" element={<StudentCareerExplorePage />} />
              <Route path="/student/route-v2" element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path="/student/growth-assets" element={<Navigate to="/growth-assets" replace />} />
              <Route path="/growth-assets" element={<Navigate to="/growth/assets" replace />} />
              <Route path="/student/practice-center" element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path={legacyRouteRedirects.studentProjectWorkspace} element={<Navigate to={learningOsRoutes.projectWorkspace} replace />} />
              <Route path={legacyRouteRedirects.studentOnboarding} element={<Navigate to={learningOsRoutes.onboarding} replace />} />
              <Route path="/student-tools" element={<Navigate to={learningOsRoutes.growth} replace />} />
              <Route path="/general" element={<AudienceSectionPage type="general" />} />
              <Route path="/professional" element={<AudienceSectionPage type="professional" />} />
              <Route path="/toolbox" element={<V2HomePage />} />
              <Route path={legacyRouteRedirects.careerPlanner} element={<LegacyExploreRedirect />} />
              <Route path={learningOsRoutes.explore} element={<CareerPlannerPage mode="home" />} />
              <Route path="/growth/explore" element={<CareerPlannerPage mode="home" />} />
              <Route path="/growth/explore/form" element={<CareerPlannerPage mode="form" />} />
              <Route path="/growth/explore/results" element={<CareerPlannerPage mode="results" />} />
              <Route path="/explore/form" element={<Navigate to="/growth/explore/form" replace />} />
              <Route path="/explore/results" element={<Navigate to="/growth/explore/results" replace />} />
              <Route path="/student/postgraduate" element={<Navigate to={learningOsRoutes.explore} replace />} />
              <Route path="/student/career" element={<Navigate to="/career-assistant" replace />} />
              <Route path="/general/ai-tools" element={<Navigate to="/tools" replace />} />
              <Route path="/general/side-hustle" element={<ComingSoonPage />} />
              <Route path="/general/content" element={<ComingSoonPage />} />
              <Route path="/general/productivity" element={<ComingSoonPage />} />
              <Route path="/general/learning" element={<ComingSoonPage />} />
              <Route path="/professional/developer" element={<Navigate to="/developer" replace />} />
              <Route path="/professional/operation" element={<ComingSoonPage />} />
              <Route path="/professional/design" element={<ComingSoonPage />} />
              <Route path="/professional/business" element={<ComingSoonPage />} />
              <Route path="/professional/education" element={<ComingSoonPage />} />
              <Route path="/professional/engineering" element={<ComingSoonPage />} />
              <Route path="/professional/medical" element={<ComingSoonPage />} />
              <Route path="/professional/public-sector" element={<ComingSoonPage />} />
              <Route path={legacyRouteRedirects.studentAiTools} element={<Navigate to={learningOsRoutes.aiTools} replace />} />
              <Route path={learningOsRoutes.aiTools} element={<AiToolsPage />} />
              <Route path={legacyRouteRedirects.studentMajorCareers} element={<Navigate to={learningOsRoutes.explore} replace />} />
              <Route path="/major-careers" element={<StudentMajorCareerPage />} />
              <Route path="/student/change-plan" element={<Navigate to={learningOsRoutes.explore} replace />} />
              <Route path={legacyRouteRedirects.studentProjectLibrary} element={<Navigate to={learningOsRoutes.projectLibrary} replace />} />
              <Route path="/student/training-assistant" element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path="/student/practice-assistant" element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path={legacyRouteRedirects.studentCompetitions} element={<CompetitionsPage />} />
              <Route path={legacyRouteRedirects.studentCertificates} element={<CertificatesPage />} />
              <Route path={learningOsRoutes.competitions} element={<CompetitionsPage />} />
              <Route path={learningOsRoutes.certificates} element={<CertificatesPage />} />
              <Route
                path="/student/job-assistant"
                element={<Navigate to="/career-assistant" replace />}
              />
              <Route
                path="/career-assistant"
                element={(
                  <RequireAuth>
                    <JobAssistantPage />
                  </RequireAuth>
                )}
              />
              <Route path="/student/tools" element={<Navigate to={learningOsRoutes.aiTools} replace />} />
              <Route path={legacyRouteRedirects.studentDashboard} element={<Navigate to={learningOsRoutes.dashboard} replace />} />
              <Route
                path={legacyRouteRedirects.studentGrowth}
                element={<Navigate to={learningOsRoutes.growth} replace />}
              />
              <Route path={legacyRouteRedirects.studentGrowthPlan} element={<Navigate to={learningOsRoutes.growthPlan} replace />} />
              <Route path={legacyRouteRedirects.studentResources} element={<Navigate to={learningOsRoutes.resource} replace />} />
              <Route path="/student/resources/:resourceId" element={<LegacyResourceRedirect />} />
              <Route path={legacyRouteRedirects.studentRoadmap} element={<Navigate to={learningOsRoutes.learning} replace />} />
              <Route path="/student/roadmap/:stageId" element={<LegacyLearningStageRedirect />} />
              <Route path={legacyRouteRedirects.studentProjects} element={<Navigate to={learningOsRoutes.project} replace />} />
              <Route path={legacyRouteRedirects.studentInterviewPack} element={<Navigate to={learningOsRoutes.interviewPack} replace />} />
              <Route path={learningOsRoutes.interviewPack} element={<InterviewPackPage />} />
              <Route path="/student/career/prepare/:direction" element={<LegacyCareerPrepareRedirect />} />
              <Route path={legacyRouteRedirects.studentProjectsList} element={<Navigate to={learningOsRoutes.project} replace />} />
              <Route path={legacyRouteRedirects.studentProjectRoadmap} element={<LegacyProjectRoadmapRedirect />} />
              <Route path={legacyRouteRedirects.studentProjectDetail} element={<LegacyProjectRedirect />} />
              <Route path={legacyRouteRedirects.studentResume} element={<Navigate to={learningOsRoutes.resume} replace />} />
              <Route path={legacyRouteRedirects.resume} element={<Navigate to={learningOsRoutes.resume} replace />} />
              <Route path={legacyRouteRedirects.resumeAnalyzer} element={<Navigate to={learningOsRoutes.resume} replace />} />
              <Route path={learningOsRoutes.resume} element={<ResumePage />} />
              <Route path={legacyRouteRedirects.interview} element={<Navigate to={learningOsRoutes.interview} replace />} />
              <Route path="/interview/:roleId" element={<InterviewPage />} />
              <Route path={learningOsRoutes.interview} element={<InterviewPage />} />
              <Route path={learningOsRoutes.interviewRole} element={<InterviewPage />} />
              <Route path="/categories/:category" element={<V2HomePage />} />
              <Route path="/alternatives" element={<V2HomePage />} />
              <Route path="/developer" element={<DeveloperHomePage />} />
              <Route path="/developer/tools" element={<DeveloperHomePage listOnly />} />
              <Route path="/developer/tools/:id" element={<DeveloperToolDetailPage />} />
              <Route path="/v1" element={<HomePage />} />
              <Route path="/weekly" element={<WeeklyPage />} />
              <Route path="/all-time" element={<AllTimePage />} />
              <Route path="/repos/:id" element={<RepoDetailPage />} />
              <Route path="/tools/:id" element={<ToolDetailPage />} />
              <Route path="/vip" element={<VipPage />} />
              <Route
                path="/vip/orders/:orderNo"
                element={(
                  <RequireAuth>
                    <VipOrderPage />
                  </RequireAuth>
                )}
              />
              <Route path="/support" element={<SupportPage />} />
              <Route path={learningOsRoutes.more} element={<MorePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/github-analysis/share" element={<GithubAnalysisSharePage />} />
              <Route path="/github-analysis/share/:id" element={<GithubAnalysisSharePage />} />
              <Route path="/github-analysis" element={<ProjectHealthPage />} />
              <Route path="/project-health" element={<ProjectHealthPage />} />
              <Route
                path="/favorites"
                element={(
                  <RequireAuth>
                    <FavoritesPage />
                  </RequireAuth>
                )}
              />
              <Route
                path="/profile"
                element={(
                  <RequireAuth>
                    <ProfilePage />
                  </RequireAuth>
                )}
              />
              <Route
                path="/my-github"
                element={(
                  <RequireAuth>
                    <MyGithubPage />
                  </RequireAuth>
                )}
              />
              <Route
                path="/admin"
                element={(
                  <RequireAuth admin>
                    <AdminPage />
                  </RequireAuth>
                )}
              />
              <Route path="/student/*" element={<Navigate to={learningOsRoutes.growth} replace />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Route>
          </Routes>
        </LanguageProvider>
      </AuthProvider>
    </HashRouter>
  )
}

export default App
